/**
 * @file Picks up a new deploy without a manual reload; the policy is docs/decisions/0002.
 * Fetches `version.json` only once a night and at the first wake after a long gap, and reloads a
 * stale tab then, never while `unsafeReasons` names pending work. A stale tab that cannot reload
 * yet shows the notice.
 */

export const CLOCK_TICK_MINUTES = 5
export const CLOCK_JUMP_MINUTES = 5
export const NIGHT_START_HOUR = 22
export const NIGHT_END_HOUR = 5
export const NIGHT_IDLE_MINUTES = 30
export const MORNING_GAP_HOURS = 6
export const RELOADED_FOR_SHA_KEY = 'blytz:reloaded-for-sha'

const MINUTE_MS = 60 * 1000
const TICK_MS = CLOCK_TICK_MINUTES * MINUTE_MS
const CONFIRM_WINDOW_MS = 2 * MINUTE_MS
// Not the store's lastActivity: that is debounced and resets on becoming visible, which hides
// the gap the morning rule measures.
const INPUT_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'touchstart',
  'wheel'
]

export type ReloadTrigger = 'tick' | 'wake'
export type ReloadRule = 'night' | 'morning'

/** A build id from Cloud Build, not a local placeholder such as `dev` or an empty string. */
export function isRealSha(sha: unknown): sha is string {
  return typeof sha === 'string' && /^[0-9a-f]{7,40}$/i.test(sha)
}

/** The local date a night started on (22:00 to 05:00 the next day), or null in the daytime. */
export function nightOf(now: number): string | null {
  const date = new Date(now)
  const hour = date.getHours()
  if (hour >= NIGHT_START_HOUR) return date.toDateString()
  if (hour >= NIGHT_END_HOUR) return null
  date.setDate(date.getDate() - 1)

  return date.toDateString()
}

/**
 * The one timing decision: which rule, if any, allows an automatic reload now. The night rule
 * applies on any trigger; the morning rule only on a wake (visible, focus, pageshow, clock jump).
 * Local time comes from the browser's `Date`.
 */
export function reloadRuleAt(
  now: number,
  lastInputAt: number,
  trigger: ReloadTrigger
): ReloadRule | null {
  const idleMs = now - lastInputAt
  const isNight = nightOf(now) !== null
  if (isNight && idleMs >= NIGHT_IDLE_MINUTES * MINUTE_MS) return 'night'
  if (trigger === 'wake' && idleMs >= MORNING_GAP_HOURS * 60 * MINUTE_MS) {
    return 'morning'
  }

  return null
}

/** The `sha` in `version.json`, or null for any failure: network, status, body or shape. */
export async function fetchServedSha(
  url: string,
  fetchImpl: typeof fetch
): Promise<string | null> {
  try {
    const response = await fetchImpl(url, { cache: 'no-store' })
    if (!response.ok) return null
    const body = await response.json()

    return isRealSha(body?.sha) ? body.sha : null
  } catch {
    return null
  }
}

export interface VersionCheckOptions {
  embeddedSha: string | undefined
  versionUrl: string
  fetchImpl: typeof fetch
  win: Window
  storage: Pick<Storage, 'getItem' | 'setItem'>
  unsafeReasons: () => string[]
  reload: () => void
  notice: { show: () => void; hide: () => void }
  reportMismatch: (embeddedSha: string, servedSha: string) => void
}

export interface VersionCheck {
  /** 'reloaded', or 'confirm' when unsafe work is pending; a second call within 2 minutes reloads. */
  reloadClicked: () => 'reloaded' | 'confirm'
  stop: () => void
}

function readKey(storage: VersionCheckOptions['storage']): string | null {
  try {
    return storage.getItem(RELOADED_FOR_SHA_KEY)
  } catch {
    return null
  }
}

function writeKey(storage: VersionCheckOptions['storage'], sha: string) {
  try {
    storage.setItem(RELOADED_FOR_SHA_KEY, sha)

    return true
  } catch {
    return false
  }
}

/** Starts the check. Returns null, and does nothing, for a build without a real commit SHA. */
export function startVersionCheck(
  options: VersionCheckOptions
): VersionCheck | null {
  const { win, storage } = options
  if (!isRealSha(options.embeddedSha)) return null
  const embeddedSha: string = options.embeddedSha
  const doc = win.document

  let servedSha: string | null = null
  let lastInputAt = Date.now()
  let lastTickAt = Date.now()
  let checkedNight: string | null = null
  let morningCheckedFor: number | null = null
  let lastFetchFailed = false
  let wakeRetryMark: number | null = null
  let confirmUntil = 0
  let mismatchReported = false
  let reloading = false

  const alreadyReloadedFor = (sha: string) => readKey(storage) === sha
  const isUnsafe = () => options.unsafeReasons().length > 0
  const ruleAllows = (trigger: ReloadTrigger) =>
    !reloading &&
    !isUnsafe() &&
    !!reloadRuleAt(Date.now(), lastInputAt, trigger)

  /** Fetches version.json; returns the newer deployed SHA, or null when current or unknown. */
  async function refresh(): Promise<string | null> {
    const sha = await fetchServedSha(options.versionUrl, options.fetchImpl)
    lastFetchFailed = !sha
    if (!sha) return null
    if (sha === embeddedSha) {
      markCurrent()

      return null
    }
    servedSha = sha
    if (alreadyReloadedFor(sha) && !mismatchReported) {
      mismatchReported = true
      options.reportMismatch(embeddedSha, sha)
    }

    return sha
  }

  function markCurrent() {
    if (servedSha === null) return
    servedSha = null
    confirmUntil = 0
    options.notice.hide()
  }

  function doReload() {
    reloading = true
    stop()
    options.reload()
  }

  // Re-checks version.json first unless the caller just fetched it; a failed fetch or a
  // same-SHA answer cancels the reload.
  async function tryAutoReload(trigger: ReloadTrigger, justFetched = false) {
    if (servedSha === null || alreadyReloadedFor(servedSha)) return
    if (!ruleAllows(trigger)) return
    const inputMark = lastInputAt

    const sha = justFetched ? servedSha : await refresh()
    if (!sha || lastInputAt !== inputMark || alreadyReloadedFor(sha)) return
    if (!ruleAllows(trigger)) return
    // Without a stored guard a still-stale reload could loop, so blocked storage never reloads.
    if (writeKey(storage, sha)) doReload()
  }

  /** The night or morning check: one fetch, a reload if a rule allows, else the notice. */
  async function checkAndReload(trigger: ReloadTrigger) {
    await refresh()
    if (lastFetchFailed) return
    checkedNight = nightOf(Date.now()) ?? checkedNight
    await tryAutoReload(trigger, true)
    if (servedSha !== null && !reloading) options.notice.show()
  }

  async function nightCheck(night: string) {
    checkedNight = night
    await checkAndReload('tick')
    if (lastFetchFailed) checkedNight = null
  }

  // The morning check runs once per idle stretch; a failed fetch (network not back after sleep)
  // retries each tick until the next input. A night return needs no fetch of its own.
  async function onWake() {
    const gap = Date.now() - lastInputAt
    if (
      gap >= MORNING_GAP_HOURS * 60 * MINUTE_MS &&
      morningCheckedFor !== lastInputAt
    ) {
      morningCheckedFor = lastInputAt
      await checkAndReload('wake')
      wakeRetryMark = lastFetchFailed ? lastInputAt : null

      return
    }
    await tryAutoReload('wake')
  }

  function onVisibility() {
    if (doc.visibilityState === 'visible') void onWake()
  }

  function onInput() {
    lastInputAt = Date.now()
  }

  // Reads only the clock, in hidden tabs too; the first tick of a night fetches once.
  function tick() {
    const now = Date.now()
    const jumped = now - lastTickAt > TICK_MS + CLOCK_JUMP_MINUTES * MINUTE_MS
    lastTickAt = now
    const retryWake = wakeRetryMark !== null && wakeRetryMark === lastInputAt
    if (retryWake) morningCheckedFor = null
    if (jumped || retryWake) {
      void onWake()

      return
    }
    const night = nightOf(now)
    if (night !== null && night !== checkedNight) {
      void nightCheck(night)

      return
    }
    void tryAutoReload('tick')
  }

  function reloadClicked(): 'reloaded' | 'confirm' {
    if (Date.now() > confirmUntil && isUnsafe()) {
      confirmUntil = Date.now() + CONFIRM_WINDOW_MS

      return 'confirm'
    }
    if (servedSha) writeKey(storage, servedSha)
    doReload()

    return 'reloaded'
  }

  const onWakeEvent = () => void onWake()
  INPUT_EVENTS.forEach(name =>
    win.addEventListener(name, onInput, { capture: true, passive: true })
  )
  doc.addEventListener('visibilitychange', onVisibility)
  win.addEventListener('focus', onWakeEvent)
  win.addEventListener('pageshow', onWakeEvent)
  const timer = win.setInterval(tick, TICK_MS)

  function stop() {
    win.clearInterval(timer)
    INPUT_EVENTS.forEach(name =>
      win.removeEventListener(name, onInput, { capture: true })
    )
    doc.removeEventListener('visibilitychange', onVisibility)
    win.removeEventListener('focus', onWakeEvent)
    win.removeEventListener('pageshow', onWakeEvent)
  }

  return { reloadClicked, stop }
}
